import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../supabase/supabase";
import { initPushNotifications } from "../capacitor/pushNotifications";
import { normalizeTeamName } from "../utils/teamName";
import { teamLogoUrl } from "../utils/teamLogo";
import { SEASON_YEAR } from "../utils/season";
import { getCached, setCached, STATIC_TTL_MS } from "../utils/queryCache";

const AuthContext = createContext();

// Builds the normalized teams map from the two halves of the team data: the
// teams table, which is static for the season, and this season's stats keyed
// by team_id.
//
// The two used to arrive as one embedded select. They are fetched separately
// so the static half can be cached to disk and the volatile half cannot: every
// column that moves during a game (game_points, game_status, record, the
// spread) lives in team_season_stats, and every column in teams is fixed once
// the season's FBS membership is set. Serving a stale record or a stale score
// from a previous launch would be wrong, not merely old.
const buildTeamsMap = (rows, statsByTeamId = {}) => {
  const map = {};
  (rows || []).forEach((t) => {
    if (!t.school) return;
    const stats = statsByTeamId[t.id] || {};
    map[normalizeTeamName(t.school)] = {
      ...t,
      logo: teamLogoUrl(t.school),
      colors: { primary: t.color, secondary: t.alternate_color },
      conference: t.conference || "Unknown",
      mascot: t.mascot || "",
      name: t.school,
      school: t.school,
      currentSeason: {
        weeklyPoints: stats.weekly_points || {},
        gamePoints: stats.game_points || 0,
        gameComplete: stats.game_complete || false,
        gameStatus: stats.game_status || null,
        record: stats.record || null,
        confRecord: stats.conf_record || null,
        atsRecord: stats.ats_record || null,
        atsWins: stats.ats_wins || 0,
        atsLosses: stats.ats_losses || 0,
        nextOpponent: stats.next_opponent || null,
        nextGameIsHome: stats.next_game_is_home || false,
        nextOpponentSpread: stats.next_opponent_spread || null,
        nextOpponentSpreadDisplay: stats.next_opponent_spread_display || null,
        totalPointsFor: stats.total_points_for || 0,
        totalPointsAgainst: stats.total_points_against || 0,
        sosRank: stats.sos_rank || null,
        isOnBye: stats.is_on_bye || false,
      },
    };
  });
  return map;
};

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);       // DB user row: first_name, last_name, is_admin, etc.
  const [seasonConfig, setSeasonConfig] = useState(null); // config.value: { currentWeek, faLocked, ... }
  const [teams, setTeams] = useState({});               // normalized teams map, keyed by school name
  const [teamsError, setTeamsError] = useState(null);   // set when the teams query returns nothing
  const [loading, setLoading] = useState(true);

  // Fetches all session-level data in parallel. Runs at boot and on sign-in.
  // userId is null for a signed-out visitor, who still needs the season config
  // and the teams map to browse the public demo league: only the users row is
  // skipped.
  const loadSessionData = async (userId) => {
    try {
      // 260-odd static rows (FBS and FCS both; see the comment on the query
      // below) that have not changed since the season was set up, refetched
      // on every cold launch, was the largest single query the app made.
      // Cached to disk for a day, it is fetched once per device per day
      // instead. The stats query beside it is never persisted.
      // v2: abandons the empty arrays the old guard allowed onto disk. See the
      // matching bump in usePreseasonStats.
      // v3: the table picked up FCS opponents (teamLabel in teamName.js) after
      // v2's cache was already on disk. Without the bump, a device that
      // cached before that backfill kept serving an FBS-only list for up to a
      // day, and every FCS opponent's TeamDrawer looked up undefined.
      const teamsKey = `teams:static:v3:${SEASON_YEAR}`;
      // Length-checked, not merely truthy. An empty array is truthy, so a
      // fetch that came back with no rows used to be persisted and then read
      // back on the next launch as a valid cache hit, which skipped the fetch
      // and left the app with no teams at all for the rest of the TTL.
      const persisted = getCached(teamsKey);
      const cachedTeams = persisted?.length ? persisted : null;

      const [
        { data: userRow },
        { data: configRow },
        { data: freshTeams },
        { data: statsRows },
      ] = await Promise.all([
        // maybeSingle keeps a missing profile or config row from rejecting the
        // whole Promise.all, which previously left teams empty and the app in a
        // half-loaded state on any account without a users row.
        userId
          ? supabase.from("users").select("*").eq("id", userId).maybeSingle()
          : Promise.resolve({ data: null }),
        supabase.from("config").select("value").eq("key", "season").maybeSingle(),
        cachedTeams
          ? Promise.resolve({ data: cachedTeams })
          : supabase.from("teams").select("*"),
        // Filtered to the current season: team_season_stats keeps a row per
        // team per year, so without this a prior season's row can win and
        // every stat on the page is last year's.
        supabase
          .from("team_season_stats")
          .select("*")
          .eq("season_year", SEASON_YEAR),
      ]);

      if (!cachedTeams && freshTeams?.length) {
        setCached(teamsKey, freshTeams, STATIC_TTL_MS, { persist: true });
      }

      const statsByTeamId = {};
      (statsRows || []).forEach((row) => { statsByTeamId[row.team_id] = row; });

      if (userRow) setUserData(userRow);
      if (configRow) setSeasonConfig(configRow.value);
      // Only overwrite the teams map with something real. A failed teams query
      // resolves to null here rather than rejecting, and rebuilding from it
      // wiped a map that was already populated, emptying every page that reads
      // it until the next sign-in.
      if (freshTeams?.length) {
        setTeams(buildTeamsMap(freshTeams, statsByTeamId));
        setTeamsError(null);
      } else {
        setTeamsError("Team data did not load");
      }
    } catch (err) {
      console.error("Failed to load session data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check for an existing session on mount (e.g. returning user, app resume)
    supabase.auth.getSession().then(({ data: { session } }) => {
      const user = session?.user ?? null;
      setCurrentUser(user);
      if (user) {
        initPushNotifications();
      }
      loadSessionData(user?.id ?? null);
    });

    // Listen for sign-in / sign-out / token refresh events during the session
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const user = session?.user ?? null;
      setCurrentUser(user);
      if (user) {
        initPushNotifications();
        loadSessionData(user.id);
      } else {
        // Signed out, or the INITIAL_SESSION event with no session at boot.
        // Only the user's own row goes: the season config and teams map are
        // the same for everyone, and a signed-out visitor browsing the demo
        // league needs them. Clearing them here raced the boot load above and
        // could empty them straight after it finished.
        setUserData(null);
      }
    });

    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Allows components (e.g. EditProfileModal) to update cached user data after a save
  // without triggering a full re-fetch
  const updateUserData = (patch) => setUserData(prev => ({ ...prev, ...patch }));

  return (
    <AuthContext.Provider value={{ currentUser, userData, seasonConfig, teams, teamsError, loading, updateUserData }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
